"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useSpring } from "motion/react";
import { useEffect } from "react";
import { chartCssVars } from "../chart-context";

// Faster spring for crosshair - responsive to mouse movement
const crosshairSpringConfig = {
  stiffness: 300,
  damping: 30
};
export type IndicatorWidth = number // Pixel width
| "line" // 1px line (default)
| "thin" // 2px
| "medium" // 4px
| "thick"; // 8px

export interface TooltipIndicatorProps {
  /** X position in pixels (center of the indicator) */
  x: number;
  /** Height of the indicator */
  height: number;
  /** Whether the indicator is visible */
  visible: boolean;
  /**
   * Width of the indicator - number (pixels) or preset.
   * Ignored if `span` is provided.
   */
  width?: IndicatorWidth;
  /**
   * Number of columns/days to span, with current point centered.
   * Requires `columnWidth` to be set.
   */
  span?: number;
  /** Width of a single column/day in pixels. Required when using `span`. */
  columnWidth?: number;
  /** Primary color at edges (10% and 90%) */
  colorEdge?: string;
  /** Secondary color at center (50%) */
  colorMid?: string;
  /** Whether to fade to transparent at 0% and 100% */
  fadeEdges?: boolean;
  /** Unique ID for the gradient */
  gradientId?: string;
}
function resolveWidth(width: IndicatorWidth): number {
  if (typeof width === "number") {
    return width;
  }
  switch (width) {
    case "line":
      return 1;
    case "thin":
      return 2;
    case "medium":
      return 4;
    case "thick":
      return 8;
    default:
      return 1;
  }
}
export function TooltipIndicator(t0) {
  const $ = _c(36);
  const {
    x,
    height,
    visible,
    width: t1,
    span,
    columnWidth,
    colorEdge: t2,
    colorMid: t3,
    fadeEdges: t4,
    gradientId: t5
  } = t0;
  const width = t1 === undefined ? "line" : t1;
  const colorEdge = t2 === undefined ? chartCssVars.crosshair : t2;
  const colorMid = t3 === undefined ? chartCssVars.crosshair : t3;
  const fadeEdges = t4 === undefined ? true : t4;
  const gradientId = t5 === undefined ? "tooltip-indicator-gradient" : t5;
  let t6;
  if ($[0] !== columnWidth || $[1] !== span || $[2] !== width) {
    t6 = span !== undefined && columnWidth !== undefined ? span * columnWidth : resolveWidth(width);
    $[0] = columnWidth;
    $[1] = span;
    $[2] = width;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const pixelWidth = t6;
  const animatedX = useSpring(x - pixelWidth / 2, crosshairSpringConfig);
  let t7;
  let t8;
  if ($[4] !== animatedX || $[5] !== pixelWidth || $[6] !== x) {
    t7 = () => {
      animatedX.set(x - pixelWidth / 2);
    };
    t8 = [x, animatedX, pixelWidth];
    $[4] = animatedX;
    $[5] = pixelWidth;
    $[6] = x;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  if (!visible) {
    return null;
  }
  const edgeOpacity = fadeEdges ? 0 : 1;
  let t9;
  if ($[9] !== colorEdge || $[10] !== edgeOpacity) {
    t9 = <stop offset="0%" style={{
      stopColor: colorEdge,
      stopOpacity: edgeOpacity
    }} />;
    $[9] = colorEdge;
    $[10] = edgeOpacity;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== colorEdge) {
    t10 = <stop offset="10%" style={{
      stopColor: colorEdge,
      stopOpacity: 1
    }} />;
    $[12] = colorEdge;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== colorMid) {
    t11 = <stop offset="50%" style={{
      stopColor: colorMid,
      stopOpacity: 1
    }} />;
    $[14] = colorMid;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== colorEdge) {
    t12 = <stop offset="90%" style={{
      stopColor: colorEdge,
      stopOpacity: 1
    }} />;
    $[16] = colorEdge;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== colorEdge || $[19] !== edgeOpacity) {
    t13 = <stop offset="100%" style={{
      stopColor: colorEdge,
      stopOpacity: edgeOpacity
    }} />;
    $[18] = colorEdge;
    $[19] = edgeOpacity;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== gradientId || $[22] !== t10 || $[23] !== t11 || $[24] !== t12 || $[25] !== t13 || $[26] !== t9) {
    t14 = <defs><linearGradient id={gradientId} x1="0%" x2="0%" y1="0%" y2="100%">{t9}{t10}{t11}{t12}{t13}</linearGradient></defs>;
    $[21] = gradientId;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = `url(#${gradientId})`;
  let t16;
  if ($[28] !== animatedX || $[29] !== height || $[30] !== pixelWidth || $[31] !== t15) {
    t16 = <motion.rect fill={t15} height={height} width={pixelWidth} x={animatedX} y={0} />;
    $[28] = animatedX;
    $[29] = height;
    $[30] = pixelWidth;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t14 || $[34] !== t16) {
    t17 = <g>{t14}{t16}</g>;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  return t17;
}
TooltipIndicator.displayName = "TooltipIndicator";
export default TooltipIndicator;